import { useInfiniteQuery } from '@tanstack/react-query'
import { Button, Divider, Flex, Select, Typography } from 'antd'
import { createStyles } from 'antd-style'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { WorkspaceApp } from 'src/api/facade/types'
import {
  permissionsListInfiniteOptions,
  type PermissionsLocalized
} from 'src/api/main'
import { readLocalized, toBaseLang, type LocalizedField } from 'src/utils'

import { THIRD_PARTY_CUSTOM_APP_TYPE } from '../constants/constants'

const { Text } = Typography

const PER_PAGE_MAX = 100

/**
 * SDK's `PermissionsLocalized` omits `label`/`description` — the backend
 * resolves them per-locale at runtime, but codegen doesn't surface them
 * (see `features/apps/components/roles/utils.ts` for the same pattern).
 */
type LocalizedPermission = PermissionsLocalized & {
  label?: LocalizedField
  description?: LocalizedField
}

type PermissionOption = {
  value: string
  label: string
  code: string
}

type Props = {
  /** App whose externally-grantable permissions are offered. */
  app?: WorkspaceApp
  /** AntD `Form.Item` feeds these through value/onChange. */
  value?: string[]
  onChange?: (codes: string[]) => void
  disabled?: boolean
  /** Defaults to the app-credential drawers' placeholder. */
  placeholder?: string
}

const useStyles = createStyles(({ token, css }) => ({
  footerDivider: css`
    margin: ${token.marginXXS}px 0;
  `,
  footer: css`
    padding: 0 ${token.paddingXXS}px ${token.paddingXXS}px;
  `
}))

/**
 * Multi-select of an app's externally-grantable permission codes (human label
 * with the raw code underneath). Shared by the app-credential create and edit
 * drawers and the public-access credential app bindings — a public token may
 * only grant permissions the app owner opened for external grant
 * (`is_externally_grantable=true`), filtered server-side.
 */
export const AppPermissionsSelect = ({
  app,
  value,
  onChange,
  disabled,
  placeholder
}: Props) => {
  const { t, i18n } = useTranslation()
  const { styles } = useStyles()
  const lang = toBaseLang(i18n.language)

  const applicationId = Number(app?.id) || 0
  const isThirdParty = app?.instanceTypeCode === THIRD_PARTY_CUSTOM_APP_TYPE
  const applicationRepositoryId = app?.instanceTypeId

  const permQuery = isThirdParty
    ? {
        application: applicationId,
        is_externally_grantable: true,
        per_page: PER_PAGE_MAX
      }
    : {
        application_repository: applicationRepositoryId,
        is_externally_grantable: true,
        per_page: PER_PAGE_MAX
      }

  const permTargetOk =
    !!app && (isThirdParty ? !!applicationId : !!applicationRepositoryId)

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery({
      ...permissionsListInfiniteOptions({ query: permQuery }),
      getNextPageParam: ({ next }) => next,
      enabled: permTargetOk
    })

  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) return
    void fetchNextPage()
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  const options: PermissionOption[] = useMemo(
    () =>
      (data?.pages?.flatMap((p) => p.results ?? []) ?? []).map((permission) => {
        const localized = permission as LocalizedPermission
        const label = readLocalized(localized.label, lang) || permission.code

        return { value: permission.code, label, code: permission.code }
      }),
    [data?.pages, lang]
  )

  const selectedCount = value?.length ?? 0
  const allSelected =
    options.length > 0 && options.every((o) => value?.includes(o.value))
  // Pages stream in one by one (the effect above auto-loads every page); only
  // offer "select all" once the full set is present so it can't quietly grant a
  // partial subset.
  const optionsSettling = isLoading || hasNextPage || isFetchingNextPage

  const selectAll = () => {
    // Union, not replace — preserve any already-picked codes (e.g. an edited
    // credential holding a code the app owner later closed for external grant).
    const merged = new Set(value ?? [])
    options.forEach((o) => merged.add(o.value))
    onChange?.(Array.from(merged))
  }

  return (
    <Select<string[], PermissionOption>
      mode="multiple"
      // Collapse overflow to a single line ("…, … +18") — selecting many
      // permissions (esp. via "Select all") otherwise grows the field into a
      // tall wall of tags that pushes the rest of the form off-screen.
      maxTagCount="responsive"
      style={{ width: '100%' }}
      value={value}
      onChange={onChange}
      loading={isLoading}
      disabled={disabled}
      options={options}
      placeholder={
        placeholder ?? t('workspace.apps.credentials.permissionsPlaceholder')
      }
      // "Select all" / "Clear" footer — picking a token's permissions one by
      // one is tedious when an app exposes many grantable codes.
      popupRender={(menu) => (
        <>
          {menu}
          <Divider className={styles.footerDivider} />
          <Flex
            justify="space-between"
            align="center"
            className={styles.footer}
          >
            <Button
              type="link"
              size="small"
              // Keep focus on the input so the dropdown doesn't close on click
              // and the change actually applies (AntD closes on outside mousedown).
              onMouseDown={(e) => e.preventDefault()}
              disabled={optionsSettling || allSelected || options.length === 0}
              onClick={selectAll}
            >
              {t('workspace.apps.credentials.selectAll')}
            </Button>
            <Button
              type="link"
              size="small"
              onMouseDown={(e) => e.preventDefault()}
              disabled={selectedCount === 0}
              onClick={() => onChange?.([])}
            >
              {t('workspace.apps.credentials.clear')}
            </Button>
          </Flex>
        </>
      )}
      // Search matches humanized label AND raw code so users can find
      // permissions either way (memorised codes still work).
      showSearch={{
        filterOption: (input, option) => {
          const q = input.toLowerCase()
          if (!option) return false

          return (
            option.label.toLowerCase().includes(q) ||
            option.code.toLowerCase().includes(q)
          )
        }
      }}
      optionRender={(option) => (
        <Flex vertical gap={0} style={{ lineHeight: 1.3 }}>
          <Text>{option.data.label}</Text>
          {option.data.code !== option.data.label ? (
            <Text
              type="secondary"
              style={{ fontSize: 11, fontFamily: 'var(--font-family-code)' }}
            >
              {option.data.code}
            </Text>
          ) : null}
        </Flex>
      )}
    />
  )
}
